import { useCallback, useEffect, useMemo } from 'react';

// Width defaults for columns that declare none. react-table also applies these as
// CSS on every cell, so drags are bounded by RESIZE_MIN_WIDTH instead.
export const defaultColumn = {
  minWidth: 30,
  width: 50,
  maxWidth: 400,
};

/**
 * Drag floor for a column that declares no `minWidth` and is not already narrower
 * than this — roughly twice a sortable header's sort-icon padding, below which its
 * label spills over.
 */
const RESIZE_MIN_WIDTH = 70;

/**
 * Swallows the single click the browser fires when a resize drag is released: it
 * targets the nearest common ancestor of where the drag started and ended, so a
 * drag released inside its own header cell lands on the sort handler.
 */
export const swallowNextClick = () => {
  const swallow = (event: MouseEvent) => event.stopPropagation();

  window.addEventListener('click', swallow, { capture: true, once: true });
  // Released without a click (a drag that ends off-window) would otherwise leave
  // the listener armed for a later, legitimate one.
  window.setTimeout(
    () => window.removeEventListener('click', swallow, true),
    0,
  );
};

/**
 * Weight units a resize drag earns per pixel of travel. Measured against the canvas,
 * which flex distributes over while the table pans, and clamped so it never amplifies.
 */
export const getWeightPerPixel = ({
  canvasWidth,
  fixedColumnsWidth,
  flexColumnsWidth,
}: {
  canvasWidth: number;
  fixedColumnsWidth: number;
  flexColumnsWidth: number;
}) => {
  const flexCanvasWidth = canvasWidth - fixedColumnsWidth;

  return flexCanvasWidth > 0
    ? Math.min(1, flexColumnsWidth / flexCanvasWidth)
    : 1;
};

export interface UseColumnResizeOptions {
  /**
   * Columns whose initial widths are computed and seeded.
   */
  columns: ReadonlyArray<{
    id?: string;
    maxWidth?: number;
    minWidth?: number;
    width?: number | string;
  }>;
  /**
   * Controlled column widths; when supplied, initial widths are not seeded.
   */
  columnWidths?: Record<string, number>;
  /**
   * Publishes seeded and resized widths back to the parent.
   */
  setColumnWidths?: (widths: Record<string, number>) => void;
}

/**
 * Owns the fixed-rem-vs-flex column width algorithm: computes and seeds initial
 * widths, and redistributes width across neighboring flex columns on resize.
 */
export const useColumnResize = ({
  columns,
  columnWidths,
  setColumnWidths,
}: UseColumnResizeOptions) => {
  const getOriginalColumnWidth = useCallback(
    column => {
      const originalColumn = columns.find(
        candidate => candidate.id === column.id,
      );
      if (!originalColumn) throw new Error(`Column ${column.id} not found`);

      let width = originalColumn.width || defaultColumn.width;
      width = typeof width === 'string' ? parseInt(width, 10) : width;

      if (originalColumn.maxWidth)
        width = Math.min(
          width,
          originalColumn.maxWidth || defaultColumn.maxWidth,
        );
      if (originalColumn.minWidth)
        width = Math.max(
          width,
          originalColumn.minWidth || defaultColumn.minWidth,
        );

      return width;
    },
    [columns],
  );

  const initialColumnWidths = useMemo(
    () =>
      columns.reduce(
        (widths, column) => {
          widths[String(column.id)] = getOriginalColumnWidth(column);
          return widths;
        },
        {} as Record<string, number>,
      ),
    [columns, getOriginalColumnWidth],
  );

  useEffect(() => {
    if (setColumnWidths && !columnWidths) {
      setColumnWidths(initialColumnWidths);
    }
  }, [columnWidths, initialColumnWidths, setColumnWidths]);

  const calcWidthIncrement = useCallback((column, width, increment) => {
    let newWidth = width + increment;
    newWidth = Math.min(newWidth, column.maxWidth ?? defaultColumn.maxWidth);

    // A column already narrower than the floor keeps the lower default: it is as
    // narrow as its own definition asked for, and flooring it where it stands would
    // freeze it — along with any drag that needs width from it.
    const floor =
      column.minWidth ??
      (width < RESIZE_MIN_WIDTH ? defaultColumn.minWidth : RESIZE_MIN_WIDTH);
    newWidth = Math.max(newWidth, floor);

    return newWidth - width;
  }, []);

  const getWidthsForColumns = useCallback(
    ({
      columnToResize,
      currentWidths,
      neighborColumns,
      newColumnWidth,
      widthDelta,
    }) => {
      const defaultIncrement =
        (widthDelta > 0 ? -1 : 1) *
        (Math.floor(Math.abs(widthDelta / neighborColumns.length)) || 1);

      const seededWidths = neighborColumns.reduce(
        (widths, column) => ({
          ...widths,
          [column.id]:
            currentWidths[column.id] || initialColumnWidths[column.id],
        }),
        {},
      );

      const distributed = neighborColumns.reduce(
        (acc, column) => {
          if (Math.abs(acc.remaining) > 0) {
            const widthToAdd = calcWidthIncrement(
              column,
              acc.widths[column.id],
              defaultIncrement,
            );
            acc.widths[column.id] += widthToAdd;
            acc.remaining += widthToAdd;
            acc.totalIncremented += widthToAdd;
          }
          return acc;
        },
        {
          remaining: Math.floor(widthDelta),
          totalIncremented: 0,
          widths: seededWidths,
        },
      );

      if (distributed.remaining === 0 || distributed.totalIncremented === 0) {
        return {
          [columnToResize.id]: newColumnWidth - distributed.remaining,
          ...distributed.widths,
        };
      }

      return getWidthsForColumns({
        columnToResize,
        currentWidths: distributed.widths,
        neighborColumns,
        newColumnWidth,
        widthDelta: distributed.remaining,
      });
    },
    [],
  );

  return { calcWidthIncrement, getWidthsForColumns, initialColumnWidths };
};

export default useColumnResize;
